import { ref } from 'vue';

/**
 * Fuso de VISUALIZAÇÃO das telas de auditoria, trilhas e Relatório de Conversões.
 *
 * Regra (operador, 2026-10-28): o servidor guarda UTC e nunca recebe instante
 * deslocado. O fuso só muda (1) como as datas aparecem e (2) como o servidor
 * calcula o intervalo do filtro — o front manda o dia de calendário
 * (`YYYY-MM-DD`) ou a hora de parede (`YYYY-MM-DDTHH:mm`) e o fuso IANA (`tz`).
 *
 * Padrão: o fuso do navegador. A escolha fica no localStorage, por pessoa.
 */

export const FUSO_PADRAO_SERVIDOR = 'America/Sao_Paulo';
const PREFIXO_CHAVE = 'whiz:fuso-visualizacao:';

export function fusoDoNavegador(): string {
    try {
        return Intl.DateTimeFormat().resolvedOptions().timeZone || FUSO_PADRAO_SERVIDOR;
    } catch {
        return FUSO_PADRAO_SERVIDOR;
    }
}

export function ehFusoValido(tz: unknown): tz is string {
    if (typeof tz !== 'string' || !tz) return false;
    try {
        new Intl.DateTimeFormat('pt-BR', { timeZone: tz });
        return true;
    } catch {
        return false;
    }
}

/** Fuso atual (reativo): quem formata dentro do template re-renderiza ao trocar. */
export const fusoVisualizacao = ref<string>(fusoDoNavegador());
let pessoaAtual: string | null = null;

function lerSalvo(pessoa: string): string | null {
    try {
        const v = localStorage.getItem(PREFIXO_CHAVE + pessoa);
        return ehFusoValido(v) ? v : null;
    } catch {
        return null;
    }
}

/** Carrega a escolha salva da pessoa (ou o fuso do navegador). */
export function carregarFusoDaPessoa(pessoa: string | null | undefined): void {
    pessoaAtual = pessoa || 'anonimo';
    fusoVisualizacao.value = lerSalvo(pessoaAtual) ?? fusoDoNavegador();
}

export function escolherFuso(tz: string): void {
    if (!ehFusoValido(tz)) return;
    fusoVisualizacao.value = tz;
    try {
        localStorage.setItem(PREFIXO_CHAVE + (pessoaAtual ?? 'anonimo'), tz);
    } catch {
        // Sem storage (aba privada): vale só nesta sessão.
    }
}

/** Offset do fuso num instante, como `UTC−03:00`. */
export function rotuloOffset(tz: string, em: Date = new Date()): string {
    try {
        const parte = new Intl.DateTimeFormat('en-US', { timeZone: tz, timeZoneName: 'longOffset' })
            .formatToParts(em)
            .find(p => p.type === 'timeZoneName')?.value ?? 'GMT';
        const offset = parte === 'GMT' ? '+00:00' : parte.replace('GMT', '');
        return `UTC${offset.replace('-', '−')}`;
    } catch {
        return '';
    }
}

const FUSOS_COMUNS = [
    'America/Sao_Paulo', 'America/Manaus', 'America/Cuiaba', 'America/Belem', 'America/Fortaleza',
    'America/Recife', 'America/Rio_Branco', 'America/Noronha', 'UTC', 'America/New_York',
    'America/Chicago', 'America/Los_Angeles', 'Europe/Lisbon', 'Europe/London', 'Europe/Madrid',
];

/** Fusos para o seletor: os mais usados primeiro, depois todos os do navegador. */
export function listaDeFusos(): string[] {
    let todos: string[] = [];
    try {
        const intl = Intl as unknown as { supportedValuesOf?: (k: string) => string[] };
        todos = intl.supportedValuesOf?.('timeZone') ?? [];
    } catch {
        todos = [];
    }
    const navegador = fusoDoNavegador();
    const comuns = [navegador, ...FUSOS_COMUNS.filter(f => f !== navegador)];
    return [...comuns, ...todos.filter(f => !comuns.includes(f))];
}

function comoData(v: string | number | Date | null | undefined): Date | null {
    if (v === null || v === undefined || v === '') return null;
    const d = v instanceof Date ? v : new Date(v);
    return Number.isNaN(d.getTime()) ? null : d;
}

/** `dd/mm/aaaa hh:mm` no fuso de visualização. Inválido devolve o texto como veio. */
export function formatarDataHora(v: string | number | Date | null | undefined, tz: string = fusoVisualizacao.value): string {
    const d = comoData(v);
    if (!d) return v ? String(v) : '';
    return d.toLocaleString('pt-BR', {
        timeZone: tz, day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
    });
}

/** `dd/mm/aaaa` no fuso de visualização. */
export function formatarData(v: string | number | Date | null | undefined, tz: string = fusoVisualizacao.value): string {
    const d = comoData(v);
    if (!d) return v ? String(v) : '';
    return d.toLocaleDateString('pt-BR', { timeZone: tz, day: '2-digit', month: '2-digit', year: 'numeric' });
}

/** `hh:mm:ss` (com `.mmm` se pedido) no fuso de visualização. */
export function formatarHora(v: string | number | Date | null | undefined, comMs = false, tz: string = fusoVisualizacao.value): string {
    const d = comoData(v);
    if (!d) return '';
    const base = d.toLocaleTimeString('pt-BR', { timeZone: tz, hour: '2-digit', minute: '2-digit', second: '2-digit' });
    return comMs ? `${base}.${String(d.getMilliseconds()).padStart(3, '0')}` : base;
}

/** `dd/mm/aaaa hh:mm:ss.mmm` no fuso de visualização. */
export function formatarDataHoraMs(v: string | number | Date | null | undefined, tz: string = fusoVisualizacao.value): string {
    const d = comoData(v);
    if (!d) return '';
    return `${formatarData(d, tz)} ${formatarHora(d, true, tz)}`;
}

/** "Hoje" (`YYYY-MM-DD`) no fuso de visualização — para defaults de filtro. */
export function hojeNoFuso(tz: string = fusoVisualizacao.value, em: Date = new Date()): string {
    return new Intl.DateTimeFormat('en-CA', { timeZone: tz, year: 'numeric', month: '2-digit', day: '2-digit' }).format(em);
}
